/**
 * Contactor JavaScript
 *
 * This file defines behaviors of
 * the Contactor block.
 */

// get dependencies
import { gsap } from "gsap"
import { MotionPathPlugin } from "gsap/MotionPathPlugin.js"
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin.js"

gsap.registerPlugin(MorphSVGPlugin, MotionPathPlugin)

class NightAndDay {
	/**
	 * Set things up
	 */
	constructor(wrap) {
		this.wrap = wrap
		this.curTime = null
		this.wrkHours = {
			isDuring: false,
			open: parseInt(wrap.dataset.open),
			close: parseInt(wrap.dataset.close),
		}
		this.timezone = "America/Denver"
		// /* DEBUG */ this.timezone = 'Asia/Tokyo';
		this.init()
	}

	/**
	 * Kick things off
	 */
	init() {
		this.trackTime()
		this.animate()
		this.watchForm()
	}

	/**
	 * Set this.curTime to the correct value
	 */
	setTime() {
		// get current time mountain time
		const mt = new Date().toLocaleString("en-US", {
			timeZone: this.timezone,
		})
		// turn that into a date we can parse
		const mtDate = new Date(mt)
		// determine if we're working now or sleeping
		const decimalTime = mtDate.getHours() + mtDate.getMinutes() / 60
		this.wrkHours.isDuring =
			decimalTime >= this.wrkHours.open &&
			decimalTime <= this.wrkHours.close
		// create pretty time
		let amPm = "am"
		let hrs = mtDate.getHours()
		if (hrs >= 12) {
			amPm = "pm"
			if (hrs >= 13) {
				hrs -= 12
			}
		}
		const min = String(mtDate.getMinutes()).padStart(2, "0")
		const sec = String(mtDate.getSeconds()).padStart(2, "0")
		const mtTime = `${hrs}:${min}:${sec} ${amPm} MT`
		// update current pretty mountain time
		this.curTime = mtTime
	}

	/**
	 * Set time initially and then every second update it
	 */
	trackTime() {
		// initialize time
		this.showTime()

		// get visual timer elements and style them
		const elTimers = this.wrap.querySelectorAll(".time-format-type")

		// update time every second
		setInterval(() => {
			this.showTime(elTimers)
		}, 1000)
	}

	/**
	 * Show the current time visually
	 */
	showTime(elTimers) {
		// get current time
		this.setTime()
		// update time visually
		elTimers?.forEach((timer) => {
			timer.innerText = this.curTime
		})
		// update styling based on working/sleeping
		const noWrk = !this.wrkHours.isDuring
		this.wrap.classList.toggle("has-2-200-background-color", noWrk)
		this.wrap.classList.toggle("has-0-900-color", noWrk)
		this.wrap.classList.toggle("is-closed", noWrk)
		this.wrap.classList.toggle("is-open", !noWrk)
	}

	/**
	 * Apply animations
	 */
	animate() {
		// animate daytime SVG
		const svgLogo = document.getElementById("opened-rabbit-logo")
		const svgRabbitOpened = document.getElementById("opened-rabbit")
		gsap.timeline({
			defaults: {
				duration: 0.15,
			},
			repeat: -1,
			repeatDelay: 2,
		})
			.to(svgLogo, { morphSVG: "#opened-rabbit-ear" }, "+=1")
			.to(svgLogo, { morphSVG: svgLogo })
			.to(svgLogo, { morphSVG: "#opened-rabbit-ear" })
			.to(svgLogo, { morphSVG: svgLogo })
			.to(svgLogo, { morphSVG: svgLogo }, "+=1")
			.to(svgLogo, { morphSVG: "#opened-rabbit-nose" })
			.to(svgLogo, { morphSVG: svgLogo })
			.to(svgLogo, { morphSVG: "#opened-rabbit-nose" })
			.to(svgLogo, { morphSVG: svgLogo })
			.to(svgLogo, { morphSVG: "#opened-rabbit-nose" })
			.to(svgLogo, { morphSVG: svgLogo })

		// animate nighttime SVG
		const rabbit = document.getElementById("closed-rabbit")
		const tl = gsap.timeline({
			defaults: {
				duration: 1,
			},
			repeat: -1,
			repeatDelay: 2,
		})
		tl.from(rabbit.querySelectorAll(".rabbit-zs"), {
			y: "10em",
			scale: "0.25",
			opacity: 0,
			ease: "power2.out",
			stagger: 0.5,
		}).to(
			rabbit.querySelectorAll(".rabbit-zs"),
			{
				y: "10em",
				opacity: 0,
				ease: "power2.in",
				stagger: 0.5,
			},
			"-=1.5"
		)

		const tl2 = gsap.timeline({
			defaults: {
				duration: 2,
			},
			repeat: -1,
			repeatDelay: 3,
		})
		const z = rabbit.querySelector(".rabbit-z")
		tl2.from(z, {
			opacity: 0,
		})
			.from(
				z,
				{
					motionPath: {
						path: "#closed-rabbit-path",
						align: "#closed-rabbit-path",
					},
				},
				"<"
			)
			.to(
				z,
				{
					opacity: 0,
				},
				"-=1.5"
			)
	}

	/**
	 * Watch form for submission message
	 */
	watchForm() {
		const elForm = this.wrap.querySelector(".wpforms-container")

		// if we have a form
		if (elForm) {
			// cache some dom nodes
			const elContactor = document.querySelector('.contactor');
			const elSubmit = elForm.querySelector('.wpforms-submit');
			// look for changes to its child nodes
			const observer = new MutationObserver((mutationList, ob) => {
				// if we have a "sending" state
				if (elSubmit.innerHTML === elSubmit.dataset.altText) {
					elContactor.classList.add("is-submitting")
					elContactor.classList.remove("is-error")
				}
				// if we have a confirmation state
				if (elForm.querySelector(
					".wpforms-confirmation-container"
				)) {
					// set state to submitted
					elContactor.classList.add("is-submitted")
					elContactor.classList.remove("is-submitting")
					elContactor.classList.remove("is-error")
					// stop observing
					ob.disconnect()
				}
				// if have error message (after submission)
				if (elForm.querySelector(
					".wpforms-error-container"
				)) {
					// set state to submitted
					elContactor.classList.add("is-error")
					elContactor.classList.remove("is-submitting")
					elContactor.classList.remove("is-submitted")
				}
			})

			observer.observe(elForm, {
				childList: true,
				subtree: true,
			})
		}
	}
}

document.querySelectorAll(".contactor")?.forEach((wrap) => {
	// eslint-disable-next-line no-new
	new NightAndDay(wrap)
})
